import React from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetHolidaysQuery } from 'store/holidays/holidays';
import { useGetLoggedEmployeeQuery } from 'store/employee/employees';
import { WorkingHours } from 'store/workingHours/workingHours';
import { LoggedEmployee } from 'store/employee/interfaces';
import { secondsInHour } from 'helpers/timeConsts';
import ContentSuspense from 'common/ContentSuspense/ContentSuspense';
import { useGetBankHolidaysQuery } from 'store/bankHolidays/bankHolidays';
import SummaryProgress from './SummaryProgress/SummaryProgress';
import useScheduleCalendarContext from '../context/useScheduleCalendarContext';
import getFilterDates from '../getFilterDates';
import getHolidaysHours from './getHolidaysHours';
import getHoursMinFormat from './getHoursMinFormat';

interface ScheduleSummaryProps {
  workingHours: WorkingHours[];
  startWeekDay: number;
}

export default function ScheduleSummary({
  workingHours,
  startWeekDay,
}: ScheduleSummaryProps) {
  const { t } = useTranslation();
  const { scheduleRanges, currentDate } = useScheduleCalendarContext();
  const { startOfWeekDay, endOfWeekDay, dateBefore, dateAfter } =
    getFilterDates({
      currentDate,
      startWeekDay,
    });

  const { data: currentUser = {} as LoggedEmployee } =
    useGetLoggedEmployeeQuery();

  const {
    data: employeeHolidays = {
      'hydra:member': [],
    },
    isLoading: employeeHolidaysLoading,
    isError: employeeHolidaysError,
  } = useGetHolidaysQuery(
    {
      employee: currentUser.id.toString(),
      'dateFrom[after]': dateAfter,
      'dateTo[before]': dateBefore,
    },
    {
      skip: !currentUser.id,
    },
  );

  const {
    data: bankHolidays = [],
    isLoading: bankHolidaysLoading,
    isError: bankHolidaysError,
  } = useGetBankHolidaysQuery();
  const displayedYear = currentDate.getFullYear();

  const plannedWorkingSeconds = scheduleRanges.reduce(
    (acc, scheduleRange) =>
      acc + (scheduleRange.timeTo - scheduleRange.timeFrom),
    0,
  );
  const loggedWorkingSeconds = workingHours.reduce(
    (acc, workingHour) => acc + workingHour.seconds,
    0,
  );

  const holidaysSeconds =
    getHolidaysHours({
      bankHolidays,
      employeeHolidays: employeeHolidays['hydra:member'],
      displayedYear,
      startOfWeek: startOfWeekDay,
      endOfWeek: endOfWeekDay,
    }) * secondsInHour;

  const toLogWorkingSeconds =
    plannedWorkingSeconds - loggedWorkingSeconds - holidaysSeconds;

  return (
    <ContentSuspense
      isLoading={employeeHolidaysLoading || bankHolidaysLoading}
      isError={employeeHolidaysError || bankHolidaysError}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        p={3}
        sx={{
          '& .MuiTypography-root': {
            userSelect: 'none',
          },
        }}
      >
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('project_tracking.planned_hours')}
          </Typography>
          <Typography variant="h6">
            {getHoursMinFormat(plannedWorkingSeconds)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack>
            <Typography variant="caption" color="text.secondary">
              {t('project_tracking.holidays')}
            </Typography>
            <Typography variant="h6" color="primary.main">
              {getHoursMinFormat(holidaysSeconds)}
            </Typography>
          </Stack>
          <Divider orientation="vertical" sx={{ height: '2.4rem' }} />
          <Stack>
            <Typography variant="caption" color="text.secondary">
              {t('project_tracking.to_be_fill')}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {getHoursMinFormat(toLogWorkingSeconds)}
            </Typography>
          </Stack>
          <Divider orientation="vertical" sx={{ height: '2.4rem' }} />
          <Stack>
            <Typography variant="caption" color="text.secondary">
              {t('project_tracking.logged_hours')}
            </Typography>
            <Typography variant="h6" color="warning.light">
              {getHoursMinFormat(loggedWorkingSeconds)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <SummaryProgress
        plannedWorkingHours={plannedWorkingSeconds}
        loggedWorkingHours={loggedWorkingSeconds}
        holidaysHours={holidaysSeconds}
      />
    </ContentSuspense>
  );
}
